import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Three behaviours the cart drawer needs that Horizon does not give it, all of
 * them consequences of the design covering the page instead of squeezing it.
 *
 * 1. Reopening after `sections/mm-cart-redirect.liquid` bounces /cart back to the
 *    previous page. Horizon's own restore path (`snippets/theme-drawer.liquid:23-60`)
 *    reads `sessionStorage['theme-drawer-open']` but returns early below 990px on
 *    purpose, so on a phone the native marker does nothing.
 *
 * 2. Closing when the shopper clicks outside, and freezing the page behind, above
 *    990px. There the dialog is opened with `show()` and not `showModal()`
 *    (`assets/theme-drawer.js:151-154`), so there is no `::backdrop`: Horizon's own
 *    `#onBackdropClick` listens on the panel (`:87`) and a modal backdrop click
 *    targets the dialog, which never happens in the non-modal case. Below 990 the
 *    dialog is modal and all of this is native, so none of it is installed.
 */

/*
 * 3. Saying why a product was not added. The drawer opens as soon as the product
 *    form asks for the add (`assets/cart-drawer.js:69-80`), and when Shopify then
 *    refuses it the only message is Horizon's under «Cantidad», behind the drawer's
 *    blur. The product form announces the refusal with `shopify:cart:error`
 *    (`assets/product-form.js:468` and `:573`), which nothing listened to; the
 *    message goes on that product's line in the drawer. Only the product form's are
 *    shown: the discount field and the gift already say theirs in place.
 */

const MODAL_BREAKPOINT = 990;
const REOPEN_KEY = 'mm-open-cart';

/** @returns {boolean} */
function consumeReopenMarker() {
  try {
    if (sessionStorage.getItem(REOPEN_KEY) !== '1') return false;
    sessionStorage.removeItem(REOPEN_KEY);
    return true;
  } catch {
    // Private mode, or site data blocked. Nothing to restore.
    return false;
  }
}

/**
 * @param {HTMLElement & { close?: () => void }} drawer
 * @param {HTMLDialogElement} dialog
 */
function installDismiss(drawer, dialog) {
  // Armed a frame after opening: the very click that opened the drawer is still
  // travelling, and without the delay it would close it again immediately.
  let armed = false;

  /** @param {PointerEvent} event */
  const onPointerDown = (event) => {
    if (!armed) return;
    if (!(event.target instanceof Element)) return;

    // The header's cart button toggles. Letting it close here too would make it
    // close and reopen on the same press, which reads as a button that does
    // nothing.
    if (event.target.closest('[on\\:click^="#cart-drawer"]')) return;

    if (!isClickedOutside(event, dialog)) return;

    drawer.close?.();
  };

  drawer.addEventListener(DrawerOpenEvent.eventName, () => {
    if (dialog.matches(':modal')) return;

    lockScroll(drawer);
    requestAnimationFrame(() => {
      armed = true;
    });
  });

  drawer.addEventListener(DrawerCloseEvent.eventName, () => {
    armed = false;
    unlockScroll(drawer);
  });

  document.addEventListener('pointerdown', onPointerDown);

  // Defensive: anything that sets [open] while the HTML is still parsing does so
  // before this module runs, so DrawerOpenEvent has already come and gone and the
  // two listeners above would never fire -- which is how a restored drawer ended up
  // open but refusing to close on an outside click. The cart drawer now carries
  // `no-persist` so Horizon's restore path no longer reaches it, but the guard stays:
  // it costs nothing and it is the same one cart-drawer.js:38 keeps.
  if (drawer.hasAttribute('open') && !dialog.matches(':modal')) {
    lockScroll(drawer);
    requestAnimationFrame(() => {
      armed = true;
    });
  }

  // Crossing the breakpoint with the drawer open hands the job back to the
  // native modal path, so the lock is released rather than left dangling.
  window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`).addEventListener('change', (event) => {
    if (!event.matches) return;
    armed = false;
    unlockScroll(drawer);
  });
}

/**
 * @param {HTMLElement} drawer
 * @param {HTMLElement} message
 */
function installAddError(drawer, message) {
  /** @type {{ variantId: string, text: string } | null} */
  let pending = null;

  /** The cart line of the refused product: its key is `<variant id>:<hash>`. */
  const findRow = () =>
    pending ? drawer.querySelector(`.cart-items__table-row[data-key^="${CSS.escape(pending.variantId)}:"]`) : null;

  // Horizon's own per-line error (cart-products.liquid:414-432), the one it uses
  // when a quantity change in the cart is refused. With several products in the
  // cart, a message at the top did not say which one it was about.
  const apply = () => {
    if (!pending) return;
    const row = findRow();
    const container = row?.querySelector('.cart-items__error');
    const text = row?.querySelector('.cart-item__error-text');

    if (container instanceof HTMLElement && text instanceof HTMLElement) {
      // Only when needed: rewriting an unchanged class still counts as a mutation,
      // and the observer below would run again every frame.
      if (text.textContent !== pending.text) text.textContent = pending.text;
      if (container.classList.contains('hidden')) container.classList.remove('hidden');
      if (!message.hidden) message.hidden = true;
      return;
    }

    // The product is not in the cart at all, so there is no line to put it on.
    if (message.textContent !== pending.text) message.textContent = pending.text;
    if (message.hidden) message.hidden = false;
  };

  const clear = () => {
    findRow()?.querySelector('.cart-items__error')?.classList.add('hidden');
    pending = null;
    message.hidden = true;
    message.textContent = '';
  };

  document.addEventListener(
    'shopify:cart:error',
    (event) => {
      const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      if (!form) return;

      const { error, code } = /** @type {{ error?: unknown, code?: string }} */ (/** @type {unknown} */ (event));
      // A network failure carries the browser's own English text; a refusal from
      // Shopify carries its message, already in the shopper's language.
      const text = code === 'SERVICE_UNAVAILABLE' || typeof error !== 'string' ? message.dataset.fallback : error;
      const variantId = /** @type {HTMLInputElement | null} */ (form.querySelector('form [name="id"]'))?.value;
      if (!text || !variantId) return;

      pending = { variantId, text };
      apply();
    },
    // Capture: the event reaches the document whether or not it bubbles.
    true
  );

  // A refused add re-renders the drawer a moment later (it is resolved without
  // sections, so the cart fetches a fresh render), and the morph puts the line's
  // `hidden` back. The message is put back until the next add or the drawer closes.
  let queued = false;
  new MutationObserver(() => {
    if (!pending || queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      apply();
    });
  }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  document.addEventListener('shopify:cart:lines-update', clear, true);
  drawer.addEventListener(DrawerCloseEvent.eventName, clear);
}

/*
 * 4. Keeping the drawer open when its last line is removed (2026-10-30). Horizon
 *    runs `on:click="/onLineItemRemove"` from a capturing listener on the document
 *    and, for the last line, swaps in the empty template right away. Its own
 *    backdrop check then runs on the dialog (`assets/theme-drawer.js:106-113`) and
 *    asks whether the clicked element is inside the panel: the «Eliminar» that was
 *    clicked is no longer in the document, so it counts as a click outside and the
 *    drawer closed instead of showing «Tu carrito está vacío» (measured: dialog.close
 *    called from #closeDialog on that click). Stopping the click at the dialog in
 *    the capture phase lets the removal run, which already happened above, and keeps
 *    it from reaching that check.
 */

/** @param {HTMLDialogElement} dialog */
function installRemoveGuard(dialog) {
  dialog.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('.cart-items__remove')) {
        event.stopPropagation();
      }
    },
    true
  );
}

customElements.whenDefined('theme-drawer').then(() => {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (!drawer) return;

  const dialog = drawer.querySelector('dialog');
  if (dialog instanceof HTMLDialogElement) {
    installDismiss(drawer, dialog);
    installRemoveGuard(dialog);
  }

  const message = drawer.querySelector('.mm-cart-drawer-error');
  if (message instanceof HTMLElement) installAddError(drawer, message);


  if (consumeReopenMarker() && !drawer.hasAttribute('open')) {
    drawer.open();
  }
});
